import React from 'react';
import moment from 'moment';

import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { get, isEmpty } from 'lodash';
import { Tooltip } from 'antd';

import { IAccount, IClientCash, ICondoGuests, ICondoUnit, ICondoUnits } from '@share/common-types';
import { getReserveUnitUrl, getSelectedCurrency, isCertificateApplied } from '@share/utils';
import { renderNumber, UrlUtils } from '@share/utils';
import { Currency, ModalCancellationPolicies } from '@components';
import { RootState } from '@share/utils';
import { IUnitsSearchState, setSelectedCondoReviewClientCash, setSelectedCondoReviewClientCashStr } from '@store/slices';
import { ICondoDetailsState, ICondosState, IMarginatorState, condosActions, marginatorActions } from '@share/store/slices';
import { ILoginState } from '@share/store/slices';
import { CondoMatchTypeEnum } from '@share/common-types';
import { CONDO_DATES_LABEL, CONDO_IS_FLEXIBLE_LABEL } from '@share/constants';
import { WalletPriceType, getWalletPrices } from '@utils';
import { RefundableEnum } from '@common-types';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBan, faCalendarDays, faCircleInfo, faLock, faMoon, faUsers } from '@fortawesome/free-solid-svg-icons';

import { ModalUnitDetailsNew } from '../../modal-unit-details-new';

import './style.scss';

interface IMapStateToProps {
  condosStore: ICondosState;
  condoGuestsStore: ICondoGuests;
  condoDetailsStore: ICondoDetailsState;
  unitsSearchStore: IUnitsSearchState;
  marginatorStore: IMarginatorState;
  loginStore: ILoginState;
}

interface IMapDispatchToProps {
  setSelectedCondoSearchClientCash: (selectedClientCahs: IClientCash) => void;
  setSelectedCondoReviewClientCash: (selectedClientCash: number) => void;
  setSelectedCondoReviewClientCashStr: (clientCash: string) => void;
  setMarginatorCommission: (commission: number) => void;
}

interface IProps extends IMapStateToProps, IMapDispatchToProps, RouteComponentProps {
  unit: ICondoUnit;
  condoId: number;
  isOnlyAlternative: boolean;
  isWalletSearch: boolean | undefined;
  isStaticDetail: boolean;
  certificateId?: string;
}

interface IState {
  isModalCancellationPoliciesVisible: boolean;
  isModalVisible: boolean;
  isStrict: boolean;
  endDate: string | null | undefined;
  startDate: string | null | undefined;
}

class CondoUnitRowNewComponent extends React.Component<IProps, IState> {
  state: IState = {
    isModalCancellationPoliciesVisible: false,
    isModalVisible: false,
    isStrict: false,
    startDate: null,
    endDate: null,
  };

  componentDidMount(): void {
    const values = UrlUtils.getValues();
    if (values[CONDO_IS_FLEXIBLE_LABEL] && values[CONDO_DATES_LABEL]) {
      const { endDate, startDate } = values[CONDO_DATES_LABEL] as { startDate: string; endDate: string };
      const isStrict: boolean = values[CONDO_IS_FLEXIBLE_LABEL] === '0' && !isEmpty(startDate) && !isEmpty(endDate);

      const startDateFormatted = startDate ? get(startDate.split('T'), '[0]') : null;
      const endDateFormatted = endDate ? get(endDate.split('T'), '[0]') : null;

      this.setState({ isStrict, endDate: endDateFormatted, startDate: startDateFormatted });
    }
  }

  changeMatchTypeForUnit = (unit: ICondoUnit, units: ICondoUnits[]) => {
    return units.map((item) => {
      return {
        ...item,
        availibilities: item.availibilities.map((availibility) => {
          if (
            unit.dateRange.from === availibility.dateRange.from &&
            unit.dateRange.to === availibility.dateRange.to
          ) {
            return { ...availibility, matchType: CondoMatchTypeEnum.Exact };
          }
          return availibility;
        }),
      };
    });
  };

  showCancellationPolicies = (): void => {
    this.setState({ isModalCancellationPoliciesVisible: true });
  };

  handleCancelModalCancellationPolicies = (): void => {
    this.setState({ isModalCancellationPoliciesVisible: false });
  };

  toggleDetailsModal = (): void => {
    this.setState({ isModalVisible: !this.state.isModalVisible });
  };

  handleReviewCondo = async (marginatorCommission: number, accountName: string | null | undefined, condoId: number, unitId: string, walletPrices: any) => {
    const { isStaticDetail, loginStore, condosStore, history } = this.props;
    const { selectedCondoSearchClientCash } = condosStore;
    const { account } = loginStore;

    const displayWalletSavings = account?.walletWalletSavings && (walletPrices?.priceSavings > 0 || walletPrices?.maxClientCashAmountAllow > 0) && !!walletPrices?.maxClientCashAmountAllow;

    const selectedPropertyClientCash = displayWalletSavings ? walletPrices?.maxClientCashPermited : selectedCondoSearchClientCash?.selectedPropertyClientCash;

    this.props.setMarginatorCommission(marginatorCommission);

    await this.props.setSelectedCondoReviewClientCash(selectedPropertyClientCash);
    await this.props.setSelectedCondoReviewClientCashStr(selectedPropertyClientCash ? selectedPropertyClientCash.toString() : '');
    await this.props.setSelectedCondoSearchClientCash({ ...selectedCondoSearchClientCash, selectedPropertyReviewClientCash: selectedPropertyClientCash });

    history.push(getReserveUnitUrl(accountName, condoId, unitId, isStaticDetail));
  };

  render(): React.ReactNode {
    const { unit, condoId, loginStore, condoDetailsStore, condoGuestsStore, condosStore, marginatorStore, certificateId } = this.props;
    const { isModalCancellationPoliciesVisible, isModalVisible, isStrict, endDate, startDate } = this.state;

    const firstAvailability = unit.availibilities?.[0];
    const maxWalletClientCash = firstAvailability?.maxWalletClientCash;

    const {
      dateRange,
      price,
      publicPrice,
      unitId,
      name,
      cancellationPolicyText,
      bedroomsCount,
      bathroomsCount,
      maxOccupancy,
      refundability,
      totalNights,
      sleepingArrangements,
    } = unit;

    const { account, userWalletData } = loginStore;
    const { condo } = condoDetailsStore;
    const { selectedCondoSearchClientCash } = condosStore;

    const value = marginatorStore.value || 0;

    const fromDateFormatted = dateRange.from ? moment(dateRange.from, 'yyyy-MM-DDTHH:mm:ss').format('yyyy-MM-DD') : null;
    const toDateFormatted = dateRange.from ? moment(dateRange.to, 'yyyy-MM-DDTHH:mm:ss').format('yyyy-MM-DD') : null;

    const isSameDate = isStrict ? startDate === fromDateFormatted && endDate === toDateFormatted : false;

    const marginatorPrice = price / (1 - (value / 100));
    const marginatorCommission = marginatorPrice - price;

    const factor = Math.pow(10, 0);
    const savings = (!!marginatorPrice && !!publicPrice && publicPrice - marginatorPrice >= 0) ? publicPrice - marginatorPrice : null;
    const savingsPercent = !!savings && !!publicPrice ? Math.round(((savings / publicPrice) * 100) * factor) / factor : 0;

    const priceInt = marginatorPrice;

    const walletPrices = getWalletPrices(account, userWalletData, selectedCondoSearchClientCash, priceInt, maxWalletClientCash, WalletPriceType.Details);
    const finalPriceCalculated = priceInt - (walletPrices?.clientCashAmountApplied ? walletPrices?.clientCashAmountApplied : 0);
    const finalPriceMax = priceInt - (walletPrices?.maxClientCashAmountAllow ? walletPrices?.maxClientCashAmountAllow : 0);

    const currency = getSelectedCurrency(account);

    const displayWalletSavings = account?.walletWalletSavings && (walletPrices?.priceSavings > 0 || walletPrices?.maxClientCashAmountAllow > 0) && !!walletPrices?.maxClientCashAmountAllow;

    const finalPrice = displayWalletSavings ? finalPriceMax : finalPriceCalculated;

    const isRefundability = refundability === RefundableEnum.Refundable;
    const certificateApplied = isCertificateApplied(account as IAccount, certificateId);

    const beds = (sleepingArrangements || []).filter(Boolean);
    const bedsText = beds.length ? beds.join(' · ') : '';

    const checkInText = dateRange?.from ? moment(dateRange.from).format('MMM D') : '';
    const checkOutText = dateRange?.to ? moment(dateRange.to).format('MMM D') : '';

    const onReserve = () => this.handleReviewCondo(marginatorCommission, account?.name, condoId, unitId, walletPrices);

    return (
      <div className={`unit-card ${isSameDate ? 'same-dates' : ''}`}>
        {isSameDate ? (
          <div className="unit-card__same-dates-icon">
            <Tooltip title={<FormattedMessage id="condos.your.selection" />} placement="top">
              <FontAwesomeIcon icon={faCircleInfo} />
            </Tooltip>
          </div>
        ) : null}

        <div className="unit-card__header">
          <h4 className="unit-card__name">
            <span className="unit-card__name-text" title={name}>{name}</span>
            <span
              className="unit-card__more-info"
              onClick={this.toggleDetailsModal}
            >
              <FormattedMessage id="more.info" />
            </span>
          </h4>
          <button
            type="button"
            className="unit-card__more-link"
            onClick={this.toggleDetailsModal}
          >
            {bedroomsCount > 0 ? `${bedroomsCount}BR` : <FormattedMessage id="more.info" />}
          </button>
        </div>

        <div className="unit-card__badges">
          <span className="unit-card__beds-badge">
            {[
              bedroomsCount > 0 ? (
                <FormattedMessage
                  key="br"
                  id="bedrooms.count"
                  values={{ count: bedroomsCount }}
                />
              ) : null,
              bathroomsCount > 0 ? (
                <FormattedMessage
                  key="ba"
                  id="bathrooms.count"
                  values={{ count: bathroomsCount }}
                />
              ) : null,
              bedsText ? <span key="beds">{bedsText}</span> : null,
            ]
              .filter(Boolean)
              .reduce<React.ReactNode[]>((acc, node, i) => {
                if (i > 0) acc.push(<span key={`sep-${i}`}> · </span>);
                acc.push(node);
                return acc;
              }, [])}
          </span>
          {!certificateApplied && (
          <span
            className={`unit-card__refundable ${isRefundability ? 'ref' : 'non-ref'}`}
            onClick={this.showCancellationPolicies}
          >
            <FontAwesomeIcon icon={isRefundability ? faLock : faBan} />
            <FormattedMessage id={isRefundability ? 'refundable' : 'non.refundable'} />
          </span>
          )}
        </div>

        <div className="unit-card__info-line">
          <div className="unit-card__info-item">
            <FontAwesomeIcon icon={faCalendarDays} />
            <span>{checkInText} → {checkOutText}</span>
          </div>
          <div className="unit-card__info-item">
            <FontAwesomeIcon icon={faMoon} />
            <FormattedMessage id="nights" values={{ count: totalNights }} />
          </div>
          <div className="unit-card__info-item">
            <FontAwesomeIcon icon={faUsers} />
            <FormattedMessage id="guests.count" values={{ count: maxOccupancy }} />
          </div>
        </div>

        <div className="unit-card__divider" />

        <div className="unit-card__footer">
          <div className="unit-card__price-block">
            <div className="unit-card__price-line">
              <div className="unit-card__price-total">
                <Currency currency={currency} />
                {renderNumber(Math.floor(finalPrice))}
              </div>
              <div className="unit-card__price-taxes">
                <FormattedMessage id="abbreviated.price.includes.taxes" />
              </div>
            </div>
            {(!!savingsPercent && savingsPercent > 0 && !displayWalletSavings) ? (
              <div className="unit-card__price-savings">
                <FormattedMessage id={account?.forceMemberWording ? 'member.save' : 'save'} /> {savingsPercent}%
              </div>
            ) : null}
            {(displayWalletSavings && walletPrices?.priceSavings > 0) ? (
              <div className="unit-card__price-savings">
                <FormattedMessage id="save.up.to" values={{ number: walletPrices?.priceSavings ? walletPrices?.priceSavings : '<1' }} />
              </div>
            ) : null}
          </div>

          <button type="button" className="unit-card__reserve" onClick={onReserve}>
            <FormattedMessage id="reserve" />
          </button>
        </div>

        <ModalUnitDetailsNew
          condo={condo?.condoDetails}
          unit={unit}
          visible={isModalVisible}
          onCancel={this.toggleDetailsModal}
          walletPrices={walletPrices}
          adultsCount={condoGuestsStore?.adultsCount}
          childrenCount={condoGuestsStore?.kidsCount}
          certificateApplied={certificateApplied}
          savings={savings}
          savingsPercent={savingsPercent}
          onReserve={() => {
            this.toggleDetailsModal();
            onReserve();
          }}
        />

        <ModalCancellationPolicies
          isForCondo={true}
          cancellationPoliciesText={cancellationPolicyText}
          visible={isModalCancellationPoliciesVisible}
          onCancel={this.handleCancelModalCancellationPolicies}
        />
      </div>
    );
  }
}

const mapStateToProps = (state: RootState): IMapStateToProps => {
  return {
    condosStore: state.condosStore,
    condoDetailsStore: state.condoDetailsStore,
    condoGuestsStore: state.condoGuestsStore,
    unitsSearchStore: state.unitsSearchStore,
    marginatorStore: state.marginatorStore,
    loginStore: state.loginStore,
  };
};

const mapDispatchToProps: IMapDispatchToProps = {
  setSelectedCondoSearchClientCash: condosActions.setSelectedCondoSearchClientCash,
  setSelectedCondoReviewClientCash,
  setSelectedCondoReviewClientCashStr,
  setMarginatorCommission: marginatorActions.setCommission,
};

export const CondoUnitRowNew = connect(mapStateToProps, mapDispatchToProps)(withRouter(CondoUnitRowNewComponent));
